import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { getRoleDefinitions } from '@/features/roles/api';
import { authStore } from '@/services/auth/store';
import { Role as StandardRole } from '@customerio/hydra-permissions/roles';
import type { WorkspacePermission } from '@customerio/hydra-permissions';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';

type RoleDefinition = {
  id: number;
  name: string;
  workspacePermissions: Set<WorkspacePermission>;
};

export function useRoleDefinitions(): Record<number, RoleDefinition> | null {
  const auth = useStore(authStore);

  const rolesQuery = useQuery({
    queryKey: ['role_definitions', auth.account?.id ?? 0],
    queryFn: async () => {
      if (auth.account == null) {
        return null;
      }

      return await getRoleDefinitions(auth.account.id);
    },
    staleTime: Infinity,
  });

  if (rolesQuery.data == null) {
    return null;
  }

  const { role_definitions } = rolesQuery.data;
  // The spec types the permission-map values as plain strings; they are WorkspacePermission values.
  const permissions =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    rolesQuery.data.permissions as Record<number, WorkspacePermission>;

  return role_definitions.reduce(
    (accumulator, roleDefinition) => {
      accumulator[roleDefinition.id] = {
        id: roleDefinition.id,
        name: roleDefinition.name,
        workspacePermissions: new Set(
          roleDefinition.permission_ids
            .map((permissionId) => permissions[permissionId])
            .filter((permission) => permission != null),
        ),
      };
      return accumulator;
    },
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    {} as Record<number, RoleDefinition>,
  );
}

/**
 * The user's role in an explicit workspace. Callers on routes without a
 * `/workspaces/<id>` segment (settings, billing) can't use the URL-derived
 * variant below — they resolve a workspace some other way (e.g.
 * `useCurrentWorkspace()`, which falls back to the last/default workspace)
 * and pass its id here.
 */
export function useUserRoleForWorkspace(workspaceId: number | null): { name: string; redacted: boolean } | null {
  const auth = useStore(authStore);
  const userRoles = auth.user?.roles ?? null;

  if (userRoles == null) {
    return null;
  }

  const userWorkspaceRole = userRoles.find((role) => role.workspace_id === workspaceId);

  if (userWorkspaceRole == null) {
    return null;
  }

  return {
    name: userWorkspaceRole.role_name,
    redacted: userWorkspaceRole.role_name.includes('-redact-on'),
  };
}

export function useUserRoleForCurrentWorkspace(): { name: string; redacted: boolean } | null {
  return useUserRoleForWorkspace(getWorkspaceIdFromCurrentUrl());
}

export function isStandardRole(role: string): role is StandardRole {
  return Object.values<string>(StandardRole).includes(role);
}
